import { notifyCreditExhaustion } from "@tribunalea/ui/credit-exhaustion";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000";

function apiError(payload, status) {
  notifyCreditExhaustion(payload);
  const error = new Error(
    payload?.error || "No pudimos completar la operación.",
  );
  error.code = payload?.code || "request_failed";
  error.status = status;
  return error;
}

export function createApi(tokenGetter) {
  const headers = async (json = false) => {
    const token = tokenGetter ? await tokenGetter() : null;
    return {
      ...(json ? { "content-type": "application/json" } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    };
  };

  async function request(path, options = {}) {
    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      credentials: "include",
      headers: {
        ...(await headers(
          Boolean(options.body) && !(options.body instanceof FormData),
        )),
        ...options.headers,
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw apiError(payload, response.status);
    return payload.data;
  }

  async function blob(path) {
    const response = await fetch(`${API_URL}${path}`, {
      credentials: "include",
      headers: await headers(false),
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw apiError(payload, response.status);
    }
    return response.blob();
  }

  return {
    listLawyers({
      circumscription,
      name = "",
      registration = "",
      page = 1,
      limit = 12,
      sessionId,
    }) {
      const parameters = new URLSearchParams({
        circumscription,
        page: String(page),
        limit: String(limit),
        sessionId,
      });
      if (name.trim()) parameters.set("name", name.trim());
      if (registration.trim()) {
        parameters.set("registration", registration.trim());
      }
      return request(`/api/civita/lawyers?${parameters}`);
    },
    listDocuments: () => request("/api/civita/documents"),
    getDocument: (id) => request(`/api/civita/documents/${id}`),
    createDocument(files, question = "") {
      const form = new FormData();
      files.forEach((file) => form.append("files", file));
      if (question.trim()) form.append("question", question.trim());
      return request("/api/civita/documents", {
        method: "POST",
        body: form,
      });
    },
    analyzeDocument: (id) => request(
      `/api/civita/documents/${id}/analyze`,
      { method: "POST" },
    ),
    retryDocument: (id) => request(
      `/api/civita/documents/${id}/retry`,
      { method: "POST" },
    ),
    deleteDocument: (id) => request(
      `/api/civita/documents/${id}`,
      { method: "DELETE" },
    ),
    getDocumentBlob: (id) => blob(`/api/civita/documents/${id}/file`),
    listClaims: () => request("/api/civita/claims"),
    getClaim: (id) => request(`/api/civita/claims/${id}`),
    createClaim({ story, recipientHint = "", files = [] }) {
      const form = new FormData();
      form.append("story", story.trim());
      if (recipientHint.trim()) form.append("recipientHint", recipientHint.trim());
      files.forEach((file) => form.append("files", file));
      return request("/api/civita/claims", {
        method: "POST",
        body: form,
      });
    },
    analyzeClaim: (id) => request(
      `/api/civita/claims/${id}/analyze`,
      { method: "POST" },
    ),
    generateClaim: (id, input) => request(
      `/api/civita/claims/${id}/generate`,
      { method: "POST", body: JSON.stringify(input) },
    ),
    retryClaim: (id) => request(
      `/api/civita/claims/${id}/retry`,
      { method: "POST" },
    ),
    deleteClaim: (id) => request(
      `/api/civita/claims/${id}`,
      { method: "DELETE" },
    ),
    getClaimSourceBlob: (claimId, sourceId) => blob(
      `/api/civita/claims/${claimId}/sources/${sourceId}/file`,
    ),
    listConversationPreparations: () => request(
      "/api/civita/conversation-preparations",
    ),
    getConversationPreparation: (id) => request(
      `/api/civita/conversation-preparations/${id}`,
    ),
    createConversationPreparation: (input) => request(
      "/api/civita/conversation-preparations",
      { method: "POST", body: JSON.stringify(input) },
    ),
    analyzeConversationPreparation: (id) => request(
      `/api/civita/conversation-preparations/${id}/analyze`,
      { method: "POST" },
    ),
    retryConversationPreparation: (id) => request(
      `/api/civita/conversation-preparations/${id}/retry`,
      { method: "POST" },
    ),
    deleteConversationPreparation: (id) => request(
      `/api/civita/conversation-preparations/${id}`,
      { method: "DELETE" },
    ),
    listLegitimacyChecks: () => request("/api/civita/legitimacy-checks"),
    getLegitimacyCheck: (id) => request(`/api/civita/legitimacy-checks/${id}`),
    createLegitimacyCheck({ files = [], ...input }) {
      const form = new FormData();
      Object.entries(input).forEach(([key, value]) => {
        if (typeof value === "string") form.append(key, value.trim());
      });
      files.forEach((file) => form.append("files", file));
      return request("/api/civita/legitimacy-checks", {
        method: "POST",
        body: form,
      });
    },
    analyzeLegitimacyCheck: (id) => request(
      `/api/civita/legitimacy-checks/${id}/analyze`,
      { method: "POST" },
    ),
    retryLegitimacyCheck: (id) => request(
      `/api/civita/legitimacy-checks/${id}/retry`,
      { method: "POST" },
    ),
    deleteLegitimacyCheck: (id) => request(
      `/api/civita/legitimacy-checks/${id}`,
      { method: "DELETE" },
    ),
    getLegitimacySourceBlob: (checkId, sourceId) => blob(
      `/api/civita/legitimacy-checks/${checkId}/sources/${sourceId}/file`,
    ),
    listSigningReviews: () => request("/api/civita/signing-reviews"),
    getSigningReview: (id) => request(`/api/civita/signing-reviews/${id}`),
    createSigningReview({
      files,
      purpose,
      promised = "",
      concern = "",
      pressure = "none",
    }) {
      const form = new FormData();
      files.forEach((file) => form.append("files", file));
      form.append("purpose", purpose.trim());
      if (promised.trim()) form.append("promised", promised.trim());
      if (concern.trim()) form.append("concern", concern.trim());
      form.append("pressure", pressure);
      return request("/api/civita/signing-reviews", {
        method: "POST",
        body: form,
      });
    },
    analyzeSigningReview: (id) => request(
      `/api/civita/signing-reviews/${id}/analyze`,
      { method: "POST" },
    ),
    retrySigningReview: (id) => request(
      `/api/civita/signing-reviews/${id}/retry`,
      { method: "POST" },
    ),
    deleteSigningReview: (id) => request(
      `/api/civita/signing-reviews/${id}`,
      { method: "DELETE" },
    ),
    getSigningReviewSourceBlob: (reviewId, sourceId) => blob(
      `/api/civita/signing-reviews/${reviewId}/sources/${sourceId}/file`,
    ),
    listCases: () => request("/api/civita/cases"),
    searchCases(query, page = 1) {
      const parameters = new URLSearchParams({ q: query.trim(), page: String(page) });
      return request(`/api/civita/cases/search?${parameters}`);
    },
    createCase(input) {
      return request("/api/civita/cases", {
        method: "POST",
        body: JSON.stringify(input),
      });
    },
    getCase: (id) => request(`/api/civita/cases/${id}`),
    explainCase: (id) => request(
      `/api/civita/cases/${id}/explain`,
      { method: "POST" },
    ),
    refreshCase: (id) => request(
      `/api/civita/cases/${id}/refresh`,
      { method: "POST" },
    ),
    retryCase: (id) => request(
      `/api/civita/cases/${id}/retry`,
      { method: "POST" },
    ),
    markCaseSeen: (id) => request(
      `/api/civita/cases/${id}/seen`,
      { method: "POST" },
    ),
    deleteCase: (id) => request(
      `/api/civita/cases/${id}`,
      { method: "DELETE" },
    ),
    async getCaseDocument(caseId, { actId, attachmentId = null }) {
      const response = await fetch(`${API_URL}/api/civita/cases/${caseId}/document`, {
        method: "POST",
        credentials: "include",
        headers: await headers(true),
        body: JSON.stringify({ actId, attachmentId }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw apiError(payload, response.status);
      }
      if (response.headers.get("content-type")?.includes("application/pdf")) {
        return {
          status: "pdf",
          blob: await response.blob(),
          fileName: decodeURIComponent(
            response.headers.get("content-disposition")?.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
              || "actuacion.pdf",
          ),
        };
      }
      const payload = await response.json();
      return payload.data;
    },
  };
}
